/** Maps the active i18n language onto a BCP-47 tag for `toLocaleDateString`. */
export function dateLocaleFor(language: string): string {
  return language.startsWith("en") ? "en-GB" : "sv-SE";
}

/** Short axis label for a period, e.g. "13 jul". */
export function periodLabel(periodStart: string, locale: string): string {
  const d = new Date(periodStart);
  if (Number.isNaN(d.getTime())) return periodStart;
  return d.toLocaleDateString(locale, { day: "numeric", month: "short" });
}

const DAY_MS = 24 * 60 * 60 * 1000;

function utcDay(iso: string): Date | null {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** ISO 8601 week number of a UTC date. */
export function isoWeek(d: Date): number {
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = Date.UTC(t.getUTCFullYear(), 0, 1);
  return Math.ceil(((t.getTime() - yearStart) / DAY_MS + 1) / 7);
}

/**
 * Axis label for one check-in period (`periodEnd` is exclusive): the week
 * number for a Monday-to-Sunday week ("v. 38" / "W38"), otherwise the day
 * range ("14-20 sep", or "28 sep-4 okt" across a month).
 */
export function periodRangeLabel(
  period: { periodStart: string; periodEnd: string },
  locale: string,
): string {
  const start = utcDay(period.periodStart);
  const endExclusive = utcDay(period.periodEnd);
  if (!start || !endExclusive) return period.periodStart;
  const days = Math.round((endExclusive.getTime() - start.getTime()) / DAY_MS);
  if (days === 7 && start.getUTCDay() === 1) {
    return locale.startsWith("en") ? `W${isoWeek(start)}` : `v. ${isoWeek(start)}`;
  }
  return dayRange(start, new Date(endExclusive.getTime() - DAY_MS), locale);
}

/**
 * A visible caption naming one check-in period: the week number plus its days
 * for a Monday-to-Sunday week ("v. 38 · 14-20 sep"), otherwise the day range.
 * The days stay visible so nobody mistakes the reported week for the running one.
 */
export function periodCaption(
  period: { periodStart: string; periodEnd: string },
  locale: string,
): string {
  const label = periodRangeLabel(period, locale);
  const start = utcDay(period.periodStart);
  const endExclusive = utcDay(period.periodEnd);
  if (!start || !endExclusive || !/^(v\. |W)\d+$/.test(label)) return label;
  return `${label} · ${dayRange(start, new Date(endExclusive.getTime() - DAY_MS), locale)}`;
}

/** The full period for a tooltip, e.g. "14-20 sep. 2026". */
export function periodRangeTitle(
  period: { periodStart: string; periodEnd: string },
  locale: string,
): string {
  const start = utcDay(period.periodStart);
  const endExclusive = utcDay(period.periodEnd);
  if (!start || !endExclusive) return period.periodStart;
  const last = new Date(endExclusive.getTime() - DAY_MS);
  return `${dayRange(start, last, locale)} ${last.getUTCFullYear()}`;
}

function dayRange(start: Date, last: Date, locale: string): string {
  const fmt = (d: Date, withMonth: boolean) =>
    d.toLocaleDateString(locale, {
      day: "numeric",
      ...(withMonth ? { month: "short" } : {}),
      timeZone: "UTC",
    });
  if (start.getTime() === last.getTime()) return fmt(start, true);
  const sameMonth = start.getUTCMonth() === last.getUTCMonth();
  return `${fmt(start, !sameMonth)}-${fmt(last, true)}`;
}
